import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';

/**
 * PO Compliance Alerts + Exhausted PO banners — shared between the main
 * Dashboard and any scoped dashboard (e.g. Private Deliveries).
 */
export const POAlertBanners = ({ poAlerts = [], exhaustedPos = [], onViewProject, onViewAllAlerts, onViewPurchaseOrders }) => (
  <>
    {poAlerts.length > 0 && (
      <Card className="border-orange-200 bg-orange-50/50" data-testid="po-compliance-alerts">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2 text-orange-800">
            <AlertTriangle className="w-5 h-5" />
            PO Compliance Alerts
          </CardTitle>
          <CardDescription className="text-orange-700">
            Training projects requiring PO attachment (5-day deadline before first session)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {poAlerts.slice(0, 3).map((alert, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-orange-100/50 ${
                  alert.severity === 'critical' ? 'bg-red-100 border-red-200' :
                  alert.severity === 'warning' ? 'bg-orange-100' : 'bg-yellow-50'
                }`}
                onClick={() => onViewProject && onViewProject(alert.project_id)}
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-4 h-4 ${alert.severity === 'critical' ? 'text-red-600' : 'text-orange-600'}`} />
                  <div>
                    <span className="font-medium">{alert.project_title}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      First session: {alert.first_session_date}
                    </span>
                  </div>
                </div>
                <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                  {alert.message}
                </Badge>
              </div>
            ))}
            {poAlerts.length > 3 && (
              <Button variant="ghost" className="w-full text-orange-700" onClick={onViewAllAlerts}>
                View all {poAlerts.length} alerts
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )}

    {exhaustedPos.length > 0 && (
      <Card className="border-red-400 bg-red-50" data-testid="exhausted-po-alert">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2 text-red-900">
            <AlertTriangle className="w-5 h-5" />
            {exhaustedPos.length} PO{exhaustedPos.length > 1 ? 's' : ''} Exhausted — Awaiting Extension
          </CardTitle>
          <CardDescription className="text-red-700">
            These POs have fully consumed their budget. New sessions cannot be billed until an Extension PO is added.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {exhaustedPos.map(po => (
              <button
                key={po.id}
                type="button"
                onClick={onViewPurchaseOrders}
                className="flex items-center gap-2 text-sm bg-white border border-red-200 rounded-full px-3 py-1.5 text-red-800 font-medium hover:bg-red-100 transition-colors"
                data-testid={`exhausted-po-${po.id}`}
              >
                <span>{po.po_number}</span>
                <span className="text-muted-foreground text-xs">{po.tsp_name}</span>
                <Badge variant="destructive" className="text-xs">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(po.consolidated_total_amount ?? po.total_amount ?? 0)} used
                </Badge>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    )}
  </>
);

export default POAlertBanners;
